/*!
 * Girlbasket Theme v1.0.0
 * Copyright (c) 2026 Om. All rights reserved.
 *
 * This file is part of the Girlbasket theme, licensed only for use on the
 * Girlbasket store (girlbasket.myshopify.com / girlbasket.com). Copying,
 * modifying, redistributing or reselling this file, in whole or in part,
 * without written permission from Om is prohibited. See LICENSE.md.
 *
 * File: assets/gb-collection.js
 */
(() => {
  'use strict';

  const GB = window.GB;
  if (!GB || !GB.events) return;

  /* ---------------------------------------------------------------------
     <gb-facets>: filters, sorting and pagination without page reloads
     --------------------------------------------------------------------- */
  class GbFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.formId;

      this.onChange = GB.debounce((event) => {
        const form = event.target.form;
        if (!form || form.id !== this.formId) return;
        this.render(this.urlFromForm(form));
      }, 350);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', (event) => {
        if (event.target.id !== this.formId) return;
        event.preventDefault();
        this.render(this.urlFromForm(event.target));
      });

      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-gb-facet-link], .gb-pagination a');
        if (link) {
          event.preventDefault();
          this.render(link.href, { scroll: link.closest('.gb-pagination') !== null });
          return;
        }

        const more = event.target.closest('[data-gb-show-more]');
        if (more) more.closest('.gb-facet').classList.toggle('is-expanded');

        const toggle = event.target.closest('[data-gb-filter-toggle]');
        if (toggle) this.toggleFilters(toggle);
      });

      this.onPopState = (event) => {
        if (event.state && event.state.gbFacets) this.render(window.location.href, { push: false });
      };
      window.addEventListener('popstate', this.onPopState);

      // Hide sold-out products by default.
      if (this.hasAttribute('data-auto-available') && !window.location.search.includes('filter.v.availability')) {
        const input = this.querySelector('input[name="filter.v.availability"][value="1"]');
        if (input) {
          const url = new URL(window.location.href);
          url.searchParams.set('filter.v.availability', '1');
          this.render(url.toString(), { replace: true });
        }
      }
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    toggleFilters(button) {
      const layout = this.querySelector('.gb-collection-layout');
      const drawer = document.getElementById(button.dataset.gbFilterToggle);
      const sidebar =
        GB.isDesktop() &&
        layout &&
        !layout.classList.contains('gb-collection-layout--drawer');

      if (sidebar) {
        const startsClosed = layout.classList.contains('gb-collection-layout--sidebar-closed');
        const isHidden = startsClosed
          ? !layout.classList.contains('is-filters-shown')
          : layout.classList.contains('is-filters-hidden');
        layout.classList.toggle('is-filters-hidden', !isHidden);
        layout.classList.toggle('is-filters-shown', isHidden);
        button.setAttribute('aria-expanded', String(isHidden));
      } else if (drawer) {
        drawer.open(button);
      }
    }

    urlFromForm(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value === '') return;
        params.append(key, value);
      });
      params.delete('page');
      return `${window.location.pathname}?${params.toString()}`;
    }

    async render(url, { push = true, replace = false, scroll = false } = {}) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading');

      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(fetchUrl, { signal: this.controller.signal });
        const html = await response.text();
        const doc = GB.parseHtml(html);
        this.swap(doc);

        const cleanUrl = new URL(url, window.location.origin);
        cleanUrl.searchParams.delete('section_id');
        if (replace) history.replaceState({ gbFacets: true }, '', cleanUrl);
        else if (push) history.pushState({ gbFacets: true }, '', cleanUrl);

        if (scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        GB.events.emit('facets:updated', { url: cleanUrl.toString() });
      } catch (error) {
        if (error.name !== 'AbortError') window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    swap(doc) {
      const openGroups = new Set(
        [...this.querySelectorAll('.gb-facet')].filter((group) => group.open).map((group) => group.id)
      );
      const expanded = new Set(
        [...this.querySelectorAll('.gb-facet.is-expanded')].map((group) => group.id)
      );
      const activeId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;

      this.querySelectorAll('[data-gb-facet-replace]').forEach((target) => {
        const fresh = doc.getElementById(target.id);
        if (fresh) target.innerHTML = fresh.innerHTML;
      });

      this.querySelectorAll('.gb-facet').forEach((group) => {
        if (openGroups.has(group.id)) group.open = true;
        if (expanded.has(group.id)) group.classList.add('is-expanded');
      });

      // Keep the sort dropdown in step with the new results.
      const freshSort = doc.querySelector('.gb-sort select');
      const sort = this.querySelector('.gb-sort select');
      if (freshSort && sort) sort.value = freshSort.value;

      if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
      GB.observeReveal(this);
    }
  }
  customElements.define('gb-facets', GbFacets);

  /* ---------------------------------------------------------------------
     <gb-price-range>: dual slider kept in step with two number fields
     --------------------------------------------------------------------- */
  class GbPriceRange extends HTMLElement {
    connectedCallback() {
      this.max = Number(this.dataset.max) || 0;
      this.rangeMin = this.querySelector('[data-gb-range-min]');
      this.rangeMax = this.querySelector('[data-gb-range-max]');
      this.numberMin = this.querySelector('[data-gb-number-min]');
      this.numberMax = this.querySelector('[data-gb-number-max]');

      this.rangeMin.addEventListener('input', () => this.fromRange('min'));
      this.rangeMax.addEventListener('input', () => this.fromRange('max'));
      this.rangeMin.addEventListener('change', () => this.commit());
      this.rangeMax.addEventListener('change', () => this.commit());
      this.numberMin.addEventListener('input', () => this.fromNumber());
      this.numberMax.addEventListener('input', () => this.fromNumber());
      this.paint();
    }

    fromRange(edge) {
      let low = Number(this.rangeMin.value);
      let high = Number(this.rangeMax.value);
      if (low > high) {
        if (edge === 'min') low = high;
        else high = low;
        this.rangeMin.value = low;
        this.rangeMax.value = high;
      }
      this.numberMin.value = low > 0 ? low : '';
      this.numberMax.value = high < this.max ? high : '';
      this.paint();
    }

    fromNumber() {
      this.rangeMin.value = this.numberMin.value || 0;
      this.rangeMax.value = this.numberMax.value || this.max;
      this.paint();
    }

    // Range inputs have no name, so tell the form that a field changed.
    commit() {
      this.numberMin.dispatchEvent(new Event('change', { bubbles: true }));
    }

    paint() {
      const low = (Number(this.rangeMin.value) / this.max) * 100 || 0;
      const high = (Number(this.rangeMax.value) / this.max) * 100 || 100;
      this.style.setProperty('--gb-range-low', `${low}%`);
      this.style.setProperty('--gb-range-high', `${high}%`);
    }
  }
  customElements.define('gb-price-range', GbPriceRange);
})();
